import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserCog, Ban, CheckCircle2, LogIn, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthProvider";
import { useT } from "@/i18n";
import { Section, StatCard, ConfigResource, SettingsForm, Toggle, Field, input } from "@/admin/ui";
import ConfirmActionDialog from "@/components/common/ConfirmActionDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const badge = (s) => ({ active: "bg-success/10 text-success", inactive: "bg-secondary text-muted-foreground", blocked: "bg-destructive/10 text-destructive" }[s] || "bg-secondary");
const boolField = (k) => ({ key: k, type: "checkbox" });
const yesNo = (i, t) => (i.active ? t("admin.yes") : t("admin.no"));

/* ---------------- Dashboard ---------------- */
export function AdminDashboard() {
  const t = useT();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" />;
  const cards = [
    ["users", d.users], ["merchants", d.merchants], ["shops", d.shops], ["products", d.products],
    ["creditsToday", d.credits_today], ["creditsMonth", d.credits_month],
    ["aiCalls", d.ai_calls], ["avgStoreScore", d.avg_store_score],
  ];
  return (
    <Section title={t("admin.dashboard.title")} subtitle={t("admin.dashboard.subtitle")}>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={t("admin.dashboard.costMonth")} value={`$${d.cost_month}`} hint={t("admin.dashboard.perMerchant", { v: `$${d.cost_per_merchant}` })} accent />
        <StatCard label={t("admin.dashboard.mrr")} value={`$${d.mrr}`} hint={t("admin.dashboard.arr", { v: `$${d.arr}` })} accent />
        <StatCard label={t("admin.dashboard.freeToPaid")} value={`${d.free_to_paid}%`} />
        <StatCard label={t("admin.dashboard.storeScore")} value={d.avg_store_score} />
        {cards.map(([k, v]) => <StatCard key={k} label={t(`admin.dashboard.${k}`)} value={v} />)}
      </div>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-5">
          <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">{t("admin.dashboard.providersUsed")}</h3>
          {d.providers_used.length ? d.providers_used.map((p) => (
            <div key={p.name} className="flex justify-between py-1 text-sm"><span className="capitalize">{p.name}</span><span className="font-mono text-brand">{p.count}</span></div>
          )) : <p className="text-sm text-muted-foreground">{t("admin.dashboard.noAiCalls")}</p>}
        </div>
        <div className="rounded-lg border border-border bg-card p-5">
          <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">{t("admin.dashboard.topLlms")}</h3>
          {d.models_used.length ? d.models_used.map((p) => (
            <div key={p.name} className="flex justify-between py-1 text-sm"><span className="font-mono">{p.name}</span><span className="font-mono text-brand">{p.count}</span></div>
          )) : <p className="text-sm text-muted-foreground">{t("admin.dashboard.noAiCalls")}</p>}
        </div>
      </div>
    </Section>
  );
}

/* ---------------- Users ---------------- */
export function AdminUsers() {
  const t = useT();
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [edit, setEdit] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); api.get("/admin/config/plans").then(({ data }) => setPlans(data)); }, []);

  const act = async (u, action) => { await api.post(`/admin/users/${u.id}/action`, { action }); toast.success(t("admin.done")); load(); };
  const impersonate = async (u) => {
    await api.post(`/admin/users/${u.id}/impersonate`);
    toast.success(t("admin.users.impersonating", { email: u.email }));
    await checkAuth(); navigate("/dashboard");
  };
  const del = async () => { await api.delete(`/admin/users/${confirmDel.id}`); toast.success(t("admin.deleted")); load(); };
  const save = async () => { await api.put(`/admin/users/${edit.id}`, { name: edit.name, role: edit.role, status: edit.status, plan_id: edit.plan_id, credits: parseInt(edit.credits) || 0 }); toast.success(t("admin.saved")); setEdit(null); load(); };

  const statusLabel = (s) => t(`admin.users.status${(s || "active").charAt(0).toUpperCase()}${(s || "active").slice(1)}`);

  return (
    <Section title={t("admin.users.title")} subtitle={t("admin.users.subtitle")}>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr><th className="p-3">{t("admin.users.user")}</th><th className="p-3">{t("admin.users.role")}</th><th className="p-3">{t("admin.users.status")}</th><th className="p-3">{t("admin.users.credits")}</th><th className="p-3 text-right">{t("admin.actions")}</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-border last:border-0 hover:bg-secondary/30" data-testid={`user-row-${u.id}`}>
                <td className="p-3"><p className="font-medium">{u.name}</p><p className="text-xs text-muted-foreground">{u.email}</p></td>
                <td className="p-3"><span className="font-mono text-xs">{u.role}</span></td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${badge(u.status)}`}>{statusLabel(u.status)}</span></td>
                <td className="p-3 font-mono">{u.credits ?? 0}</td>
                <td className="p-3">
                  <div className="flex justify-end gap-1">
                    <button title={t("admin.users.tipEdit")} onClick={() => setEdit({ ...u })} data-testid={`user-edit-${u.id}`} className="rounded-md p-2 text-muted-foreground hover:bg-secondary"><UserCog className="h-4 w-4" /></button>
                    {u.status === "blocked"
                      ? <button title={t("admin.users.tipActivate")} onClick={() => act(u, "activate")} data-testid={`user-activate-${u.id}`} className="rounded-md p-2 text-muted-foreground hover:bg-success/10 hover:text-success"><CheckCircle2 className="h-4 w-4" /></button>
                      : <button title={t("admin.users.tipBlock")} onClick={() => act(u, "block")} data-testid={`user-block-${u.id}`} className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"><Ban className="h-4 w-4" /></button>}
                    <button title={t("admin.users.tipImpersonate")} onClick={() => impersonate(u)} data-testid={`user-impersonate-${u.id}`} className="rounded-md p-2 text-muted-foreground hover:bg-brand/10 hover:text-brand"><LogIn className="h-4 w-4" /></button>
                    <button title={t("admin.users.tipDelete")} onClick={() => setConfirmDel(u)} data-testid={`user-delete-${u.id}`} className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="user-edit-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("admin.users.editTitle")}</DialogTitle><DialogDescription>{edit?.email}</DialogDescription></DialogHeader>
          {edit && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div><label className="mb-1 block text-xs text-muted-foreground">{t("admin.users.name")}</label><input className={input} value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="user-name-input" /></div>
              <div><label className="mb-1 block text-xs text-muted-foreground">{t("admin.users.role")}</label>
                <select className={input} value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })} data-testid="user-role-select">
                  {["super_admin", "admin", "support", "merchant", "viewer"].map((r) => <option key={r} value={r}>{r}</option>)}
                </select></div>
              <div><label className="mb-1 block text-xs text-muted-foreground">{t("admin.users.status")}</label>
                <select className={input} value={edit.status || "active"} onChange={(e) => setEdit({ ...edit, status: e.target.value })} data-testid="user-status-select">
                  {["active", "inactive", "blocked"].map((r) => <option key={r} value={r}>{statusLabel(r)}</option>)}
                </select></div>
              <div><label className="mb-1 block text-xs text-muted-foreground">{t("admin.users.plan")}</label>
                <select className={input} value={edit.plan_id || ""} onChange={(e) => setEdit({ ...edit, plan_id: e.target.value })} data-testid="user-plan-select">
                  <option value="">{t("admin.none")}</option>
                  {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select></div>
              <div><label className="mb-1 block text-xs text-muted-foreground">{t("admin.users.credits")}</label><input type="number" className={input} value={edit.credits ?? 0} onChange={(e) => setEdit({ ...edit, credits: e.target.value })} data-testid="user-credits-input" /></div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEdit(null)} className="rounded-md border border-border px-4 py-2 text-sm hover:bg-secondary">{t("admin.cancel")}</button>
            <button onClick={save} data-testid="user-save" className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90">{t("admin.save")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmActionDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        title={t("admin.confirm.deleteUserTitle")}
        description={t("admin.confirm.deleteUserDesc")}
        resourceName={confirmDel?.email}
        onConfirm={del}
      />
    </Section>
  );
}

/* ---------------- Merchants ---------------- */
export function AdminMerchants() {
  const t = useT();
  const [m, setM] = useState([]);
  useEffect(() => { api.get("/admin/merchants").then(({ data }) => setM(data)); }, []);
  const heads = ["merchant", "plan", "credits", "aiUsed", "shops", "products", "avgScore"];
  return (
    <Section title={t("admin.merchants.title")} subtitle={t("admin.merchants.subtitle")}>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm" data-testid="merchants-table">
          <thead className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>{heads.map((h) => <th key={h} className="p-3">{t(`admin.merchants.${h}`)}</th>)}</tr>
          </thead>
          <tbody>
            {m.map((x) => (
              <tr key={x.id} className="border-b border-border last:border-0 hover:bg-secondary/30">
                <td className="p-3"><p className="font-medium">{x.name}</p><p className="text-xs text-muted-foreground">{x.email}</p></td>
                <td className="p-3">{x.plan}</td><td className="p-3 font-mono">{x.credits}</td><td className="p-3 font-mono">{x.ai_consumed}</td>
                <td className="p-3 font-mono">{x.shops}</td><td className="p-3 font-mono">{x.products}</td><td className="p-3 font-mono text-brand">{x.avg_score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

/* ---------------- Config pages ---------------- */
export const AdminPlans = () => (
  <ConfigResource resource="plans" titleKey="admin.plans.title" subtitleKey="admin.plans.subtitle"
    columns={[{ key: "name", labelKey: "admin.plans.colName" }, { key: "price_monthly", labelKey: "admin.plans.colMonthly", render: (i) => `$${i.price_monthly}` }, { key: "products", labelKey: "admin.plans.colProducts" }, { key: "ai_credits", labelKey: "admin.plans.colCredits" }, { key: "active", labelKey: "admin.plans.colActive", render: yesNo }]}
    defaults={{ name: "", price_monthly: 0, price_annual: 0, shops: 1, products: 100, users: 1, ai_credits: 100, connectors: 2, storage_mb: 500, bandwidth_gb: 10, order: 99, color: "#0055FF", badge: "", description: "", active: true, shopify_import: false, woocommerce_import: false, api: false, mcp: false, acp: false, ucp: false, knowledge_graph: true, feed_ai: true, bulk_ai: false }}
    fields={[
      { key: "name", type: "text" }, { key: "description", type: "textarea" },
      { key: "price_monthly", type: "number" }, { key: "price_annual", type: "number" },
      { key: "shops", type: "number" }, { key: "products", type: "number" },
      { key: "users", type: "number" }, { key: "ai_credits", type: "number" },
      { key: "connectors", type: "number" }, { key: "storage_mb", type: "number" },
      { key: "bandwidth_gb", type: "number" }, { key: "order", type: "number" },
      { key: "color", type: "color" }, { key: "badge", type: "text" },
      boolField("active"), boolField("shopify_import"), boolField("woocommerce_import"),
      boolField("api"), boolField("mcp"), boolField("acp"), boolField("ucp"),
      boolField("knowledge_graph"), boolField("feed_ai"), boolField("bulk_ai"),
    ]} />
);

export const AdminCreditPacks = () => (
  <ConfigResource resource="credit-packs" titleKey="admin.packs.title" subtitleKey="admin.packs.subtitle"
    columns={[{ key: "name", labelKey: "admin.packs.colName" }, { key: "credits", labelKey: "admin.packs.colCredits" }, { key: "price", labelKey: "admin.packs.colPrice", render: (i) => `$${i.price}` }, { key: "bonus", labelKey: "admin.packs.colBonus" }, { key: "active", labelKey: "admin.packs.colActive", render: yesNo }]}
    defaults={{ name: "", credits: 100, price: 5, bonus: 0, order: 99, visible: true, active: true }}
    fields={[{ key: "name", type: "text" }, { key: "credits", type: "number" }, { key: "price", type: "number" }, { key: "bonus", type: "number" }, { key: "order", type: "number" }, boolField("visible"), boolField("active")]} />
);

export const AdminProviders = () => (
  <ConfigResource resource="ai-providers" titleKey="admin.providers.title" subtitleKey="admin.providers.subtitle"
    columns={[{ key: "name", labelKey: "admin.providers.colProvider" }, { key: "priority", labelKey: "admin.providers.colPriority" }, { key: "cost_input", labelKey: "admin.providers.colIn" }, { key: "cost_output", labelKey: "admin.providers.colOut" }, { key: "active", labelKey: "admin.providers.colActive", render: yesNo }]}
    defaults={{ name: "", key: "", api_key: "", active: true, priority: 9, timeout: 30, retry: 2, cost_input: 0, cost_output: 0, models: [] }}
    fields={[{ key: "name", type: "text" }, { key: "key", type: "text" }, { key: "api_key", type: "text" }, { key: "priority", type: "number" }, { key: "timeout", type: "number" }, { key: "retry", type: "number" }, { key: "cost_input", type: "number" }, { key: "cost_output", type: "number" }, boolField("active")]} />
);

export const AdminModels = () => (
  <ConfigResource resource="ai-models" titleKey="admin.models.title" subtitleKey="admin.models.subtitle"
    columns={[{ key: "label", labelKey: "admin.models.colTask" }, { key: "provider", labelKey: "admin.models.colProvider" }, { key: "model", labelKey: "admin.models.colModel" }, { key: "fallback", labelKey: "admin.models.colFallback" }, { key: "credit_cost", labelKey: "admin.models.colCredits" }, { key: "active", labelKey: "admin.models.colActive", render: yesNo }]}
    defaults={{ task: "", label: "", provider: "openai", model: "", fallback: "", temperature: 0.7, max_tokens: 1200, timeout: 30, retry: 1, credit_cost: 1, required_plan: "", active: true }}
    fields={[{ key: "task", type: "text" }, { key: "label", type: "text" }, { key: "provider", type: "select", options: ["openai", "anthropic", "gemini", "mistral", "deepseek", "openrouter", "ollama", "mock"] }, { key: "model", type: "text" }, { key: "fallback", type: "select", options: ["", "openai", "anthropic", "gemini", "mistral", "deepseek", "mock"] }, { key: "temperature", type: "number" }, { key: "max_tokens", type: "number" }, { key: "timeout", type: "number" }, { key: "retry", type: "number" }, { key: "credit_cost", type: "number" }, { key: "required_plan", type: "text" }, boolField("active")]} />
);

export const AdminPrompts = () => (
  <ConfigResource resource="prompts" titleKey="admin.prompts.title" subtitleKey="admin.prompts.subtitle"
    columns={[{ key: "name", labelKey: "admin.prompts.colName" }, { key: "task", labelKey: "admin.prompts.colTask" }, { key: "version", labelKey: "admin.prompts.colVersion" }, { key: "status", labelKey: "admin.prompts.colStatus" }]}
    defaults={{ name: "", description: "", task: "", version: 1, status: "active", providers: [], variables: [], system: "", template: "" }}
    fields={[{ key: "name", type: "text" }, { key: "task", type: "text" }, { key: "version", type: "number" }, { key: "status", type: "select", options: ["active", "draft", "inactive"] }, { key: "description", type: "textarea" }, { key: "system", type: "textarea" }, { key: "template", type: "textarea" }]} />
);

/* ---------------- AI Router ---------------- */
export function AdminAIRouter() {
  const t = useT();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/ai-router/overview").then(({ data }) => setRows(data)); }, []);
  const heads = ["task", "provider", "model", "avgCost", "avgTime", "calls", "success", "fallbacks", "errors", "fallback"];
  return (
    <Section title={t("admin.router.title")} subtitle={t("admin.router.subtitle")}>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm" data-testid="router-table">
          <thead className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>{heads.map((h) => <th key={h} className="p-3">{t(`admin.router.${h}`)}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.task} className="border-b border-border last:border-0 hover:bg-secondary/30">
                <td className="p-3 font-medium">{r.label}</td><td className="p-3 capitalize">{r.provider}</td><td className="p-3 font-mono text-xs">{r.model}</td>
                <td className="p-3 font-mono">${r.avg_cost}</td><td className="p-3 font-mono">{r.avg_time_ms}ms</td><td className="p-3 font-mono">{r.calls}</td>
                <td className="p-3 font-mono text-success">{r.success_rate}%</td>
                <td className="p-3 font-mono">{r.fallbacks ?? 0}</td>
                <td className="p-3 font-mono text-destructive">{r.errors ?? 0}</td>
                <td className="p-3 capitalize text-muted-foreground">{r.fallback || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

/* ---------------- Cost analytics ---------------- */
export function AdminCost() {
  const t = useT();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/cost-analytics").then(({ data }) => setD(data)); }, []);
  if (!d) return <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" />;
  const bars = (arr) => { const max = Math.max(...arr.map((x) => x.cost), 0.000001); return arr.map((x) => (
    <div key={x.name} className="py-1">
      <div className="flex justify-between text-sm"><span className="capitalize">{x.name}</span><span className="font-mono text-muted-foreground">${x.cost}</span></div>
      <div className="mt-1 h-1.5 rounded-full bg-border"><div className="h-full rounded-full bg-brand" style={{ width: `${(x.cost / max) * 100}%` }} /></div>
    </div>
  )); };
  return (
    <Section title={t("admin.cost.title")} subtitle={t("admin.cost.subtitle")}>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={t("admin.cost.costToday")} value={`$${d.cost_today}`} />
        <StatCard label={t("admin.cost.costMonth")} value={`$${d.cost_month}`} />
        <StatCard label={t("admin.cost.costYear")} value={`$${d.cost_year}`} />
        <StatCard label={t("admin.cost.saving")} value={`$${d.saving}`} accent />
        <StatCard label={t("admin.cost.inputTokens")} value={d.input_tokens.toLocaleString()} />
        <StatCard label={t("admin.cost.outputTokens")} value={d.output_tokens.toLocaleString()} />
        <StatCard label={t("admin.cost.avgTokens")} value={d.avg_tokens} />
        <StatCard label={t("admin.cost.avgCost")} value={`$${d.avg_cost}`} />
      </div>
      <div className="mt-6 grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-5"><h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">{t("admin.cost.byProvider")}</h3>{bars(d.by_provider)}</div>
        <div className="rounded-lg border border-border bg-card p-5"><h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">{t("admin.cost.byModel")}</h3>{bars(d.by_model)}</div>
        <div className="rounded-lg border border-border bg-card p-5"><h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">{t("admin.cost.byTask")}</h3>{bars(d.by_task)}</div>
      </div>
    </Section>
  );
}

/* ---------------- Credits consumption ---------------- */
export function AdminCredits() {
  const t = useT();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/credits/overview").then(({ data }) => setD(data)); }, []);
  if (!d) return <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" />;
  return (
    <Section title={t("admin.credits.title")} subtitle={t("admin.credits.subtitle")}>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={t("admin.credits.purchased")} value={d.purchased} />
        <StatCard label={t("admin.credits.consumed")} value={d.consumed} accent />
        <StatCard label={t("admin.credits.residual")} value={d.residual} />
        <StatCard label={t("admin.credits.realCost")} value={`$${d.real_cost}`} hint={t("admin.credits.margin", { v: `$${d.margin}` })} />
      </div>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-5"><h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">{t("admin.credits.topMerchants")}</h3>{d.top_merchants.map((x) => <div key={x.name} className="flex justify-between py-1 text-sm"><span className="truncate">{x.name}</span><span className="font-mono text-brand">{x.credits}</span></div>)}</div>
        <div className="rounded-lg border border-border bg-card p-5"><h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">{t("admin.credits.topFeatures")}</h3>{d.top_features.map((x) => <div key={x.name} className="flex justify-between py-1 text-sm"><span className="capitalize">{x.name}</span><span className="font-mono text-brand">{x.credits}</span></div>)}</div>
      </div>
    </Section>
  );
}

/* ---------------- Brand / SEO ---------------- */
export const AdminBrand = () => (
  <SettingsForm endpoint="/admin/brand" titleKey="admin.brand.title" subtitleKey="admin.brand.subtitle" testid="brand"
    fields={[{ key: "platform_name", type: "text" }, { key: "slogan", type: "text" }, { key: "logo_url", type: "image" }, { key: "logo_dark_url", type: "image" }, { key: "favicon_url", type: "image" }, { key: "primary_color", type: "color" }, { key: "font", type: "text" }, { key: "email", type: "text" }, { key: "domain", type: "text" }, { key: "social_twitter", type: "text" }, { key: "social_linkedin", type: "text" }, { key: "social_instagram", type: "text" }, { key: "footer", type: "text" }, { key: "copyright", type: "text" }, { key: "privacy_url", type: "text" }, { key: "terms_url", type: "text" }, { key: "cookie_url", type: "text" }, { key: "meta_description", type: "textarea" }]} />
);

export const AdminSEO = () => (
  <SettingsForm endpoint="/admin/seo" titleKey="admin.seo.title" subtitleKey="admin.seo.subtitle" testid="seo"
    fields={[{ key: "title", type: "text" }, { key: "description", type: "textarea" }, { key: "keywords", type: "text" }, { key: "og_image", type: "text" }, { key: "twitter_card", type: "text" }, { key: "canonical", type: "text" }, { key: "robots", type: "text" }, { key: "sitemap", type: "text" }, { key: "schema_organization", type: "text" }, { key: "analytics_id", type: "text" }, { key: "pixel_id", type: "text" }, { key: "gtm_id", type: "text" }]} />
);

/* ---------------- Appearance (Branding + SEO + GEO customization hub) ---------------- */
const BRAND_FIELDS = [
  { key: "platform_name", type: "text" }, { key: "slogan", type: "text" },
  { key: "logo_url", type: "image" }, { key: "logo_dark_url", type: "image" },
  { key: "favicon_url", type: "image" }, { key: "og_image", type: "image" },
  { key: "primary_color", type: "color" }, { key: "secondary_color", type: "color" },
  { key: "font", type: "text" }, { key: "email", type: "text" }, { key: "domain", type: "text" },
  { key: "social_twitter", type: "text" }, { key: "social_linkedin", type: "text" }, { key: "social_instagram", type: "text" },
  { key: "footer", type: "text" }, { key: "copyright", type: "text" },
  { key: "meta_description", type: "textarea" },
];
const SEO_FIELDS = [
  { key: "title", type: "text" }, { key: "description", type: "textarea" }, { key: "keywords", type: "text" },
  { key: "canonical", type: "text" }, { key: "robots", type: "text" }, { key: "twitter_card", type: "text" },
  { key: "schema_organization", type: "text" }, { key: "sitemap", type: "text" },
  { key: "analytics_id", type: "text" }, { key: "pixel_id", type: "text" }, { key: "gtm_id", type: "text" },
];
const GEO_FIELDS = [
  { key: "default_country", type: "text" }, { key: "default_language", type: "text" }, { key: "default_currency", type: "text" },
  { key: "timezone", type: "text" }, { key: "region_target", type: "text" },
  { key: "supported_countries", type: "text" }, { key: "supported_languages", type: "text" },
  { key: "business_address", type: "text" }, { key: "business_region", type: "text" },
  { key: "latitude", type: "text" }, { key: "longitude", type: "text" },
  { key: "geo_targeting_enabled", type: "checkbox" },
];

export function AdminAppearance() {
  const t = useT();
  const [brand, setBrand] = useState(null);
  const [seo, setSeo] = useState(null);
  const [geo, setGeo] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/admin/brand"), api.get("/admin/seo"), api.get("/admin/geo")])
      .then(([b, s, g]) => { setBrand(b.data || {}); setSeo(s.data || {}); setGeo(g.data || {}); })
      .catch(() => { setBrand({}); setSeo({}); setGeo({}); });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        api.put("/admin/brand", brand),
        api.put("/admin/seo", seo),
        api.put("/admin/geo", geo),
      ]);
      toast.success(t("admin.saved"));
    } catch (e) { toast.error(e.response?.data?.detail || t("admin.error")); }
    finally { setSaving(false); }
  };

  if (!brand || !seo || !geo) return <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" />;

  const setB = (k, v) => setBrand((f) => ({ ...f, [k]: v }));
  const setS = (k, v) => setSeo((f) => ({ ...f, [k]: v }));
  const setG = (k, v) => setGeo((f) => ({ ...f, [k]: v }));
  const card = (title, hint, fields, state, setter) => (
    <div className="rounded-lg border border-border bg-card p-6">
      <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
      <p className="mb-4 text-sm text-muted-foreground">{hint}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {fields.map((f) => <Field key={f.key} f={f} value={state[f.key]} onChange={setter} />)}
      </div>
    </div>
  );

  return (
    <Section title={t("admin.appearance.title")} subtitle={t("admin.appearance.subtitle")} actions={
      <button onClick={save} disabled={saving} data-testid="appearance-save" className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60">{saving ? t("common.confirm.processing") : t("admin.save")}</button>
    }>
      <div className="space-y-6">
        {card(t("admin.appearance.branding"), t("admin.appearance.brandingHint"), BRAND_FIELDS, brand, setB)}
        {card(t("admin.appearance.seo"), t("admin.appearance.seoHint"), SEO_FIELDS, seo, setS)}
        {card(t("admin.appearance.geo"), t("admin.appearance.geoHint"), GEO_FIELDS, geo, setG)}
      </div>
    </Section>
  );
}

/* ---------------- Legal (privacy / terms / cookie + cookie banner) ---------------- */
export function AdminLegal() {
  const t = useT();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/legal").then(({ data }) => setForm(data || {})).catch(() => setForm({})); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/legal", form); toast.success(t("admin.saved")); }
    catch (e) { toast.error(e.response?.data?.detail || t("admin.error")); }
    finally { setSaving(false); }
  };
  if (!form) return <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" />;
  const docs = [
    { key: "privacy", label: t("admin.legal.privacy") },
    { key: "terms", label: t("admin.legal.terms") },
    { key: "cookie", label: t("admin.legal.cookie") },
  ];
  return (
    <Section title={t("admin.legal.title")} subtitle={t("admin.legal.subtitle")} actions={
      <button onClick={save} disabled={saving} data-testid="legal-save" className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60">{saving ? t("common.confirm.processing") : t("admin.save")}</button>
    }>
      <div className="mb-6 rounded-lg border border-border bg-card p-6">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <p className="font-medium">{t("admin.legal.cookieBanner")}</p>
            <p className="text-xs text-muted-foreground">{t("admin.legal.cookieBannerHint")}</p>
          </div>
          <Toggle checked={!!form.cookie_banner_enabled} onChange={(v) => set("cookie_banner_enabled", v)} testid="legal-cookie-enabled" />
        </div>
        <label className="mb-1 block text-xs text-muted-foreground">{t("admin.legal.cookieBannerText")}</label>
        <textarea className={`${input} h-20 py-2`} value={form.cookie_banner_text || ""} onChange={(e) => set("cookie_banner_text", e.target.value)} data-testid="legal-cookie-text" />
      </div>
      <div className="space-y-6">
        {docs.map((d) => (
          <div key={d.key} className="rounded-lg border border-border bg-card p-6">
            <label className="mb-2 block text-sm font-medium">{d.label}</label>
            <textarea className={`${input} h-64 py-2 font-mono text-xs leading-relaxed`} value={form[d.key] || ""} onChange={(e) => set(d.key, e.target.value)} data-testid={`legal-${d.key}`} placeholder={t("admin.legal.placeholder")} />
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- Billing ---------------- */
export function AdminBilling() {
  const t = useT();
  const [p, setP] = useState([]);
  const load = () => api.get("/admin/billing").then(({ data }) => setP(data));
  useEffect(() => { load(); }, []);
  const toggle = async (x) => { await api.put(`/admin/billing/${x.key}`, { active: !x.active }); load(); };
  return (
    <Section title={t("admin.billing.title")} subtitle={t("admin.billing.subtitle")}>
      <div className="grid gap-3 sm:grid-cols-2">
        {p.map((x) => (
          <div key={x.key} className="flex items-center justify-between rounded-lg border border-border bg-card p-4" data-testid={`billing-${x.key}`}>
            <div><p className="font-medium">{x.label}</p><p className="text-xs text-muted-foreground">{t("admin.billing.mode", { v: x.mode })}</p></div>
            <Toggle checked={x.active} onChange={() => toggle(x)} testid={`billing-toggle-${x.key}`} />
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- Feature flags ---------------- */
export function AdminFeatureFlags() {
  const t = useT();
  const [flags, setFlags] = useState(null);
  useEffect(() => { api.get("/admin/feature-flags").then(({ data }) => setFlags(data)); }, []);
  const save = async (next) => { setFlags(next); await api.put("/admin/feature-flags", next); toast.success(t("admin.saved")); };
  if (!flags) return <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" />;
  return (
    <Section title={t("admin.flags.title")} subtitle={t("admin.flags.subtitle")}>
      <div className="grid gap-2 sm:grid-cols-2">
        {Object.entries(flags).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between rounded-lg border border-border bg-card p-4" data-testid={`flag-${k}`}>
            <span className="text-sm capitalize">{k.replace(/_/g, " ")}</span>
            <Toggle checked={v} onChange={(nv) => save({ ...flags, [k]: nv })} testid={`flag-toggle-${k}`} />
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ---------------- Audit log ---------------- */
export function AdminAudit() {
  const t = useT();
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit-log").then(({ data }) => setLogs(data)); }, []);
  const heads = ["when", "actor", "action", "target"];
  return (
    <Section title={t("admin.audit.title")} subtitle={t("admin.audit.subtitle")}>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm" data-testid="audit-table">
          <thead className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>{heads.map((h) => <th key={h} className="p-3">{t(`admin.audit.${h}`)}</th>)}</tr>
          </thead>
          <tbody>
            {logs.map((l, i) => (
              <tr key={i} className="border-b border-border last:border-0">
                <td className="p-3 font-mono text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</td>
                <td className="p-3">{l.actor_email || "—"}</td>
                <td className="p-3 font-mono text-xs">{l.action}</td>
                <td className="p-3 text-muted-foreground">{l.target || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

/* ---------------- Settings ---------------- */
export function AdminSettings() {
  const t = useT();
  return (
    <Section title={t("admin.settings.title")} subtitle={t("admin.settings.subtitle")}>
      <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
        {t("admin.settings.body")}
      </div>
    </Section>
  );
}
